/**
 * ContactRoiModal — View an ROI (Release of Information) for a contact.
 *
 * ROI authorizations are assigned from the client chart (Add other document).
 * This modal supports viewing authorization details and revoking when needed.
 */
import {
  Alert,
  Badge,
  Button,
  Divider,
  Group,
  List,
  Modal,
  Paper,
  Stack,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMedplum } from '@medplum/react';
import { revokeContactRoi } from '../../../lib/contactRoiWorkflow';
import {
  IconAlertCircle,
  IconCalendar,
  IconFileX,
  IconInfoCircle,
  IconX,
} from '@tabler/icons-react';
import { useState } from 'react';
import {
  ROI_PERMISSION_LABELS,
  type Contact,
  type RoiPermissionKey,
} from '../data/contactTypes';
import { resolveRoiStatus } from '../utils/contactRoiUtils';

function RoiStatusBadge({ status }: { status: ReturnType<typeof resolveRoiStatus> }): JSX.Element {
  const map = {
    active: { color: 'teal', label: 'Active ROI' },
    expired: { color: 'orange', label: 'Expired ROI' },
    pending: { color: 'blue', label: 'Pending ROI' },
    none: { color: 'gray', label: 'No ROI' },
  } as const;
  const { color, label } = map[status];
  return (
    <Badge variant="light" color={color} size="sm">
      {label}
    </Badge>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function RoiViewContent({ contact }: { contact: Contact }): JSX.Element {
  const roi = contact.roi;
  const status = resolveRoiStatus(roi);

  if (!roi) {
    return (
      <Paper withBorder p="sm" radius="md"
        style={{ borderColor: 'var(--mantine-color-gray-3)', backgroundColor: 'var(--mantine-color-gray-0)' }}
      >
        <Group gap="xs" wrap="nowrap">
          <IconFileX size={18} color="var(--mantine-color-gray-5)" style={{ flexShrink: 0 }} />
          <div>
            <Text size="sm" fw={600} c="gray.6">No ROI on file</Text>
            <Text size="xs" c="gray.5">
              Assign the Release of Information Consent for Coordination of Care form from the client chart.
            </Text>
          </div>
        </Group>
      </Paper>
    );
  }

  const grantedKeys = (Object.keys(ROI_PERMISSION_LABELS) as RoiPermissionKey[]).filter(
    (k) => roi.permissions[k],
  );

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <RoiStatusBadge status={status} />
        {roi.dateSigned && (
          <Group gap={4}>
            <IconCalendar size={14} color="var(--mantine-color-gray-5)" />
            <Text size="xs" c="gray.6">Signed {formatDate(roi.dateSigned)}</Text>
          </Group>
        )}
      </Group>

      {roi.expirationDate && (
        <Text size="sm" c="gray.7">
          Expires: {formatDate(roi.expirationDate)}
        </Text>
      )}

      <div>
        <Text size="sm" fw={600} mb={4}>Authorized information</Text>
        {grantedKeys.length === 0 ? (
          <Text size="sm" c="gray.6">No categories recorded.</Text>
        ) : (
          <List size="sm" spacing={2}>
            {grantedKeys.map((k) => (
              <List.Item key={k}>
                {ROI_PERMISSION_LABELS[k]}
                {k === 'other' && roi.otherDescription ? ` — ${roi.otherDescription}` : ''}
              </List.Item>
            ))}
          </List>
        )}
      </div>
    </Stack>
  );
}

export interface ContactRoiModalProps {
  opened: boolean;
  onClose: () => void;
  contact: Contact;
  patientId: string;
  onSaved?: () => Promise<void>;
}

export function ContactRoiModal({
  opened,
  onClose,
  contact,
  patientId,
  onSaved,
}: ContactRoiModalProps): JSX.Element {
  const medplum = useMedplum();
  const [revoking, setRevoking] = useState(false);
  const [revokeConfirmOpen, setRevokeConfirmOpen] = useState(false);

  const contactName = `${contact.legalFirstName} ${contact.legalLastName}`;
  const status = resolveRoiStatus(contact.roi);
  const canRevokeRoi = status === 'pending' || status === 'active' || status === 'expired';
  const revokeIsPending = status === 'pending';

  const handleRevoke = async () => {
    const consentId = contact.roi?.consentId;
    if (!consentId) return;
    setRevoking(true);
    try {
      const result = await revokeContactRoi(medplum, { patientId, consentId });
      notifications.show({
        color: 'green',
        title: result.wasPending ? 'Authorization request cancelled' : 'ROI revoked',
        message: result.wasPending
          ? `The ROI request for ${contactName} was cancelled.`
          : `The ROI for ${contactName} has been revoked.`,
      });
      setRevokeConfirmOpen(false);
      onClose();
      await onSaved?.();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: revokeIsPending ? 'Error cancelling ROI' : 'Error revoking ROI',
        message: err instanceof Error ? err.message : 'An error occurred.',
      });
    } finally {
      setRevoking(false);
    }
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        title={`ROI — ${contactName}`}
        size="lg"
        centered
      >
        <Stack gap="md">
          <Alert
            icon={<IconInfoCircle size={16} />}
            color="blue"
            variant="light"
            title="What is an ROI?"
          >
            <Text size="xs">
              A Release of Information (ROI) authorizes this contact to receive medical information on
              behalf of the client. This is not the same as portal or app access.
            </Text>
          </Alert>

          <RoiViewContent contact={contact} />

          {canRevokeRoi && contact.roi?.consentId && (
            <>
              <Divider />
              <Group justify="flex-end">
                <Button
                  variant="light"
                  color="red"
                  leftSection={<IconX size={16} />}
                  onClick={() => setRevokeConfirmOpen(true)}
                  loading={revoking}
                >
                  {revokeIsPending ? 'Cancel authorization request' : 'Revoke ROI'}
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Modal>

      <Modal
        opened={revokeConfirmOpen}
        onClose={() => setRevokeConfirmOpen(false)}
        title={revokeIsPending ? 'Cancel ROI authorization request?' : 'Revoke this ROI?'}
        centered
        size="sm"
      >
        <Stack gap="md">
          <Group gap="xs" wrap="nowrap">
            <IconAlertCircle size={20} color="var(--mantine-color-orange-6)" style={{ flexShrink: 0 }} />
            <Text size="sm">
              {revokeIsPending ? (
                <>The client has not signed yet. Cancelling will withdraw this authorization request for {contactName}.</>
              ) : (
                <>The contact will no longer be authorized to receive protected health information under this ROI.</>
              )}
            </Text>
          </Group>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setRevokeConfirmOpen(false)}>
              {revokeIsPending ? 'Keep request' : 'Keep ROI'}
            </Button>
            <Button color="red" onClick={handleRevoke} loading={revoking}>
              {revokeIsPending ? 'Cancel request' : 'Revoke ROI'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
